import { useState } from "react";
import { SlidersHorizontal, X, Calendar, MapPin } from "lucide-react";
import REGIONS from "@/lib/regions.json";
import { STATES } from "@/components/RegionSelect";
import { upcomingDays, formatDMY } from "@/lib/dates";

export const TRADES = ["Carpenter", "Electrician", "Plumber", "Labourer", "Plasterer", "Roofer", "Painter", "Bricklayer", "Scaffolder", "Tiler", "HVAC", "Concreter", "Welder", "Landscaper", "Gardener", "Cleaner", "Builder"];
const NICK = { Carpenter: "Chippy", Electrician: "Sparky", Labourer: "Labourer", Plumber: "Plumber" };
const SORTS = [["", "Best match"], ["reliability", "Most reliable"], ["rate_low", "Cheapest first"], ["rate_high", "Highest rate first"]];
const CHIP = "px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors whitespace-nowrap";
const on = (a) => a ? "bg-[#FF6A00] border-[#FF6A00] text-black" : "bg-[#121212] border-white/15 text-zinc-300 hover:border-[#FF6A00] hover:text-white";

export function DateRow({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const days = upcomingDays(42);
  const today = days[0]?.iso, tomorrow = days[1]?.iso;
  const custom = value && value !== today && value !== tomorrow;
  return (
    <div data-testid="contractors-date-row">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">When do you need them?</div>
      <div className="mt-2 flex gap-2 flex-wrap">
        <button onClick={() => onChange("")} className={`${CHIP} ${on(!value)}`} data-testid="date-any">Any day</button>
        <button onClick={() => onChange(today)} className={`${CHIP} ${on(value === today)}`} data-testid="date-today">Today</button>
        <button onClick={() => onChange(tomorrow)} className={`${CHIP} ${on(value === tomorrow)}`} data-testid="date-tomorrow">Tomorrow</button>
        <button onClick={() => setOpen((o) => !o)} className={`${CHIP} inline-flex items-center gap-1.5 ${on(custom || open)}`} data-testid="date-pick"><Calendar className="w-3.5 h-3.5" /> {custom ? formatDMY(value) : "Pick a date"}</button>
      </div>
      {open && (
        <div className="mt-2 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x" data-testid="contractors-date-scroller">
          {days.map((d) => {
            const sel = value === d.iso, isToday = d.iso === today;
            const box = sel ? "bg-[#FF6A00] border-[#FF6A00] text-black" : isToday ? "bg-[#121212] border-[#FF6A00] border-2 text-white" : "bg-[#121212] border-white/15 text-white hover:border-[#FF6A00]";
            return (
              <button key={d.iso} onClick={() => { onChange(d.iso); setOpen(false); }} data-testid={`date-chip-${d.iso}`} aria-current={isToday ? "date" : undefined}
                style={isToday && !sel ? { borderColor: "#FF6A00" } : undefined}
                className={`flex-shrink-0 snap-start w-14 py-1.5 flex flex-col items-center border ${box}`}>
                <span style={isToday && !sel ? { color: "#FF6A00" } : undefined} className={`text-[9px] uppercase font-mono-data font-bold ${sel ? "text-black/70" : isToday ? "text-[#FF6A00]" : d.isWeekend ? "text-[#FF6A00]" : "text-zinc-500"}`}>{isToday ? "Today" : d.dow}</span>
                <span className="font-display font-black text-base leading-none mt-0.5">{d.day}</span>
                <span className={`text-[9px] uppercase font-mono-data ${sel ? "text-black/70" : "text-zinc-500"}`}>{d.monthShort}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TradeChips({ value, onChange }) {
  const [more, setMore] = useState(false);
  const shown = more ? TRADES : TRADES.slice(0, 6);
  return (
    <div data-testid="contractors-trade-chips">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">Which trade?</div>
      <div className="mt-2 flex gap-2 flex-wrap">
        <button onClick={() => onChange("")} className={`${CHIP} ${on(!value)}`} data-testid="trade-chip-all">All trades</button>
        {shown.map((t) => <button key={t} onClick={() => onChange(value === t ? "" : t)} className={`${CHIP} ${on(value === t)}`} data-testid={`trade-chip-${t.toLowerCase()}`}>{NICK[t] || t}</button>)}
        {!more && value && !shown.includes(value) && <button className={`${CHIP} ${on(true)}`}>{value}</button>}
        <button onClick={() => setMore((m) => !m)} className={`${CHIP} border-dashed border-white/20 text-zinc-500 hover:text-white`} data-testid="trade-chip-more">{more ? "Less" : `+${TRADES.length - 6} more`}</button>
      </div>
    </div>
  );
}

export function FiltersButton({ center, setCenter, sort, setSort, count, dateLabel }) {
  const [open, setOpen] = useState(false);
  const active = (center ? 1 : 0) + (sort ? 1 : 0);
  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono-data text-xs tracking-widest text-zinc-400" data-testid="contractors-count">// {count} {count === 1 ? "TRADIE" : "TRADIES"}{center ? ` NEAR ${center.toUpperCase()}` : ""}{dateLabel ? ` · FREE ${dateLabel.toUpperCase()}` : " · ALL REGISTERED"}</div>
        <button onClick={() => setOpen(true)} className={`${CHIP} inline-flex items-center gap-2 ${on(active > 0)}`} data-testid="filters-open"><SlidersHorizontal className="w-3.5 h-3.5" /> Filters{active ? ` · ${active}` : ""}</button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center" data-testid="filters-sheet">
          <div className="w-full sm:max-w-md bg-[#121212] border-t sm:border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><div className="font-display font-black text-2xl uppercase">Filters</div><button onClick={() => setOpen(false)} data-testid="filters-close"><X className="w-5 h-5 text-red-500" /></button></div>
            <label className="block mt-5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Region
              <div className="relative mt-1"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF6A00]" />
                <select value={center} onChange={(e) => setCenter(e.target.value)} className="w-full appearance-none bg-black border border-white/15 pl-9 pr-3 py-3 text-white text-sm focus:outline-none focus:border-[#FF6A00]" data-testid="contractors-filter-center">
                  <option value="">Anywhere</option>{STATES.map((s) => <optgroup key={s} label={s}>{REGIONS.filter((r) => r.state === s).map((r) => <option key={r.name} value={r.name}>{r.region} · within 50 km</option>)}</optgroup>)}
                </select></div>
            </label>
            <div className="mt-5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Sort by</div>
            <div className="mt-2 grid grid-cols-2 gap-2">{SORTS.map(([k, l]) => <button key={k} onClick={() => setSort(k)} className={`${CHIP} ${on(sort === k)}`} data-testid={`sort-chip-${k || "top"}`}>{l}</button>)}</div>
            <div className="mt-6 flex gap-2">
              <button onClick={() => { setCenter(""); setSort(""); }} className="flex-1 border border-white/20 text-white uppercase tracking-widest text-xs font-bold py-3 hover:border-red-500 hover:text-red-500" data-testid="filters-reset">Reset</button>
              <button onClick={() => setOpen(false)} className="flex-1 bg-[#FF6A00] text-black uppercase tracking-widest text-xs font-black py-3 hover:bg-white" data-testid="filters-done">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
